import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import sectionApi from '@/Services/sectionApi';
import siteTemplate from '@/config/siteTemplate';
import { getPageSeo } from '@/data/content';

const SLUG_TO_PAGE_KEY: Record<string, string> = {
    home: 'accueil',
    menu: 'nos-plats',
    commander: 'commander',
    services: 'services',
    contact: 'contact',
    'about-us': 'about-us',
};

const DEFAULT_TITLE = "Bivin's Kitchen | Traiteur Indien";

interface SeoData {
    title: string;
    description: string;
    keywords: string;
    robots: string;
    image: string;
    canonical: string;
    jsonLd: Record<string, unknown> | null;
}

const normalizeCanonical = (inputCanonical: string | undefined): string => {
    const raw = inputCanonical || window.location.href;
    const url = new URL(raw, window.location.origin);
    url.search = '';
    url.hash = '';
    if (url.pathname.length > 1 && url.pathname.endsWith('/')) {
        url.pathname = url.pathname.replace(/\/+$/, '');
    }
    return url.toString();
};

const applyStaticSeo = (page: string): SeoData | null => {
    const contentSlug = SLUG_TO_PAGE_KEY[page] || page;
    const seo = getPageSeo(contentSlug);
    if (!seo) return null;
    return {
        title: seo.meta_title || DEFAULT_TITLE,
        description: seo.meta_description || '',
        keywords: seo.meta_keywords || '',
        robots: seo.robots || 'index,follow',
        image: '',
        canonical: normalizeCanonical(seo.url),
        jsonLd: null,
    };
};

interface SeoMetaProps {
    page: string;
    overrides?: Partial<SeoData>;
}

const SeoMeta: React.FC<SeoMetaProps> = ({ page, overrides }) => {
    const [seo, setSeo] = useState<SeoData | null>(() => applyStaticSeo(page));

    useEffect(() => {
        const abortController = new AbortController();

        const loadSeo = async () => {
            try {
                const seoData = await sectionApi.getSeo(page || 'home', siteTemplate) as Record<string, unknown> | undefined;
                if (abortController.signal.aborted || !seoData) return;

                setSeo({
                    title: String(seoData.title ?? seo?.title ?? DEFAULT_TITLE),
                    description: String(seoData.description ?? seo?.description ?? ''),
                    keywords: String(seoData.keywords ?? seo?.keywords ?? ''),
                    robots: String(seoData.robots ?? seo?.robots ?? 'index,follow'),
                    image: String(seoData.image ?? seo?.image ?? ''),
                    canonical: normalizeCanonical(String(seoData.canonical ?? '')),
                    jsonLd: (seoData.json_ld ?? null) as Record<string, unknown> | null,
                });
            } catch {
                if (!abortController.signal.aborted) {
                    setSeo(applyStaticSeo(page));
                }
            }
        };

        loadSeo();

        return () => {
            abortController.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    const data = { ...seo, ...overrides } as SeoData;
    if (!data) return null;

    return (
        <Helmet>
            <title>{data.title || DEFAULT_TITLE}</title>
            {data.description && <meta name="description" content={data.description} />}
            {data.keywords && <meta name="keywords" content={data.keywords} />}
            <meta name="robots" content={data.robots || 'index,follow'} />
            {data.title && <meta property="og:title" content={data.title} />}
            {data.description && <meta property="og:description" content={data.description} />}
            {data.image && <meta property="og:image" content={data.image} />}
            <meta property="og:type" content="website" />
            <meta name="twitter:card" content="summary_large_image" />
            {data.title && <meta name="twitter:title" content={data.title} />}
            {data.description && <meta name="twitter:description" content={data.description} />}
            {data.image && <meta name="twitter:image" content={data.image} />}
            <link rel="canonical" href={data.canonical || window.location.href} />
            {data.jsonLd && (
                <script type="application/ld+json">{JSON.stringify(data.jsonLd)}</script>
            )}
        </Helmet>
    );
};

export default SeoMeta;
